import { useState } from 'react';
import { Dialog, DialogContent, Button, Box, Typography, DialogActions, DialogTitle, Grid } from "@mui/material";
import { Imposto } from '../../../../../../../types/api/fiscal/detalhe-nfe-entrada';
import DisplayNotFoundInfo from '../../../../../../../components/DisplayNotFindInfo';
import { TaxField } from './TaxField';
import { enqueueSnackbar } from 'notistack';
import { formatarInputValorMonetárioSemPrefixo } from '../../../../../../../helpers/masks';


export function DialogImpostosNotaFiscal({ open, handleClose, impostos, onSave }: DialogImpostosNotaFiscalProps) {

    const [editedImpostos, setEditedImpostos] = useState<Imposto>(impostos);
    const [originalImpostos] = useState<Imposto>(impostos);

    const handleValueChange = (field: keyof Imposto, value: { base_calculo: string; valor: string; aliquota: string }) => {
        setEditedImpostos(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                base_calculo: value.base_calculo,
                valor: value.valor,
                aliquota: value.aliquota
            }
        }));
    };

    const validateTax = (label: string, base: string, aliquota: string, valor: string): boolean => {

        if (!base && !aliquota && !valor) {
            return true;
        }

        const camposPreenchidos = [base, aliquota, valor].filter(v => v).length;
        if (camposPreenchidos >= 2) {
            const baseCalculo = parseFloat(base.replace(',', '.')) || 0;
            const aliquotaNum = parseFloat(aliquota.replace(',', '.')) || 0;
            const valorInformado = parseFloat(valor.replace(',', '.')) || 0;
            
            const valorCalculado = (baseCalculo * aliquotaNum) / 100;
            const diferenca = Math.abs(valorCalculado - valorInformado);
            
            if (diferenca > 0.01) {
                enqueueSnackbar(
                    `${label}: Valor incorreto. Esperado: R$ ${formatarInputValorMonetárioSemPrefixo(valorCalculado.toFixed(2))}`, 
                    { variant: "warning" }
                );
                return false;
            }
        }

        return true;
    };

    const handleApply = () => {
        const impostoLabels = {
            ipi: 'IPI',
            iss: 'ISS',
            icms: 'ICMS',
            icms_st: 'ICMS ST',
            icms_diferencial: 'ICMS Diferencial',
            pis: 'PIS',
            cofins: 'COFINS'
        };

        let hasError = false;
        (Object.keys(editedImpostos) as Array<keyof Imposto>).forEach(key => {
            const imposto = editedImpostos[key];
            if (imposto && typeof imposto === 'object') {
                const isValid = validateTax(
                    impostoLabels[key],
                    imposto.base_calculo?.toString() || '',
                    imposto.aliquota?.toString() || '',
                    imposto.valor?.toString() || ''
                );
                if (!isValid) {
                    hasError = true;
                }
            }
        });

        if (hasError) {
            return;
        }

        const updatedImpostos: Imposto = { ...editedImpostos };

        (Object.keys(updatedImpostos) as Array<keyof Imposto>).forEach(key => {
            const imposto = updatedImpostos[key];
            if (imposto && typeof imposto === 'object') {
                updatedImpostos[key] = {
                    ...imposto,
                    base_calculo: imposto.base_calculo ? parseFloat(imposto.base_calculo.toString().replace(',', '.')) : 0,
                    valor: imposto.valor ? parseFloat(imposto.valor.toString().replace(',', '.')) : 0,
                    aliquota: imposto.aliquota ? parseFloat(imposto.aliquota.toString().replace(',', '.')) : 0
                } as any;
            }
        });

        if (onSave) {
            onSave(updatedImpostos);
        }
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Adicionar impostos</DialogTitle>
            <DialogContent>
                {!impostos ? (
                    <Box>
                        <DisplayNotFoundInfo
                            label="Nenhum imposto encontrado"
                        />
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            <Grid container size={{ xs: 12 }}>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="body2" fontWeight='bold'>
                                        Todos os impostos
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 8 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 1 }}>
                                        <Typography variant="body2" fontWeight='bold' sx={{ width: 150 }}>
                                            Base do Imposto
                                        </Typography>
                                        <Typography variant="body2" fontWeight='bold' sx={{ width: 150 }}>
                                            Alíquota
                                        </Typography>
                                        <Typography variant="body2" fontWeight='bold' sx={{ width: 150 }}>
                                            Valor do Imposto
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TaxField
                                    label="1- IPI"
                                    value={{
                                        base_calculo: editedImpostos.ipi?.base_calculo?.toString() || '',
                                        valor: editedImpostos.ipi?.valor?.toString() || '',
                                        aliquota: editedImpostos.ipi?.aliquota?.toString() || ''
                                    }}
                                    originalValue={{
                                        base_calculo: originalImpostos.ipi?.base_calculo?.toString() || '',
                                        valor: originalImpostos.ipi?.valor?.toString() || '',
                                        aliquota: originalImpostos.ipi?.aliquota?.toString() || ''
                                    }}
                                    onValueChange={(value) => handleValueChange('ipi', value)}
                                />
                                <TaxField
                                    label="2- ISS"
                                    value={{
                                        base_calculo: editedImpostos.iss?.base_calculo?.toString() || '',
                                        valor: editedImpostos.iss?.valor?.toString() || '',
                                        aliquota: editedImpostos.iss?.aliquota?.toString() || ''
                                    }}
                                    originalValue={{
                                        base_calculo: originalImpostos.iss?.base_calculo?.toString() || '',
                                        valor: originalImpostos.iss?.valor?.toString() || '',
                                        aliquota: originalImpostos.iss?.aliquota?.toString() || ''
                                    }}
                                    onValueChange={(value) => handleValueChange('iss', value)}
                                />
                                <TaxField
                                    label="3- ICMS"
                                    value={{
                                        base_calculo: editedImpostos.icms?.base_calculo?.toString() || '',
                                        valor: editedImpostos.icms?.valor?.toString() || '',
                                        aliquota: editedImpostos.icms?.aliquota?.toString() || ''
                                    }}
                                    originalValue={{
                                        base_calculo: originalImpostos.icms?.base_calculo?.toString() || '',
                                        valor: originalImpostos.icms?.valor?.toString() || '',
                                        aliquota: originalImpostos.icms?.aliquota?.toString() || ''
                                    }}
                                    onValueChange={(value) => handleValueChange('icms', value)}
                                />
                                <TaxField
                                    label="4- ICMS ST"
                                    value={{
                                        base_calculo: editedImpostos.icms_st?.base_calculo?.toString() || '',
                                        valor: editedImpostos.icms_st?.valor?.toString() || '',
                                        aliquota: editedImpostos.icms_st?.aliquota?.toString() || ''
                                    }}
                                    originalValue={{
                                        base_calculo: originalImpostos.icms_st?.base_calculo?.toString() || '',
                                        valor: originalImpostos.icms_st?.valor?.toString() || '',
                                        aliquota: originalImpostos.icms_st?.aliquota?.toString() || ''
                                    }}
                                    onValueChange={(value) => handleValueChange('icms_st', value)}
                                />
                                <TaxField
                                    label="5- ICMS Diferido"
                                    value={{
                                        base_calculo: editedImpostos.icms_diferencial?.base_calculo?.toString() || '',
                                        valor: editedImpostos.icms_diferencial?.valor?.toString() || '',
                                        aliquota: editedImpostos.icms_diferencial?.aliquota?.toString() || ''
                                    }}
                                    originalValue={{
                                        base_calculo: originalImpostos.icms_diferencial?.base_calculo?.toString() || '',
                                        valor: originalImpostos.icms_diferencial?.valor?.toString() || '',
                                        aliquota: originalImpostos.icms_diferencial?.aliquota?.toString() || ''
                                    }}
                                    onValueChange={(value) => handleValueChange('icms_diferencial', value)}
                                />
                                <TaxField
                                    label="6- PIS"
                                    value={{
                                        base_calculo: editedImpostos.pis?.base_calculo?.toString() || '',
                                        valor: editedImpostos.pis?.valor?.toString() || '',
                                        aliquota: editedImpostos.pis?.aliquota?.toString() || ''
                                    }}
                                    originalValue={{
                                        base_calculo: originalImpostos.pis?.base_calculo?.toString() || '',
                                        valor: originalImpostos.pis?.valor?.toString() || '',
                                        aliquota: originalImpostos.pis?.aliquota?.toString() || ''
                                    }}
                                    onValueChange={(value) => handleValueChange('pis', value)}
                                />
                                <TaxField
                                    label="7- COFINS"
                                    value={{
                                        base_calculo: editedImpostos.cofins?.base_calculo?.toString() || '',
                                        valor: editedImpostos.cofins?.valor?.toString() || '',
                                        aliquota: editedImpostos.cofins?.aliquota?.toString() || ''
                                    }}
                                    originalValue={{
                                        base_calculo: originalImpostos.cofins?.base_calculo?.toString() || '',
                                        valor: originalImpostos.cofins?.valor?.toString() || '',
                                        aliquota: originalImpostos.cofins?.aliquota?.toString() || ''
                                    }}
                                    onValueChange={(value) => handleValueChange('cofins', value)}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{ minWidth: 100 }}
                >
                    Fechar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleApply}
                    sx={{ minWidth: 100 }}
                >
                    Aplicar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

interface DialogImpostosNotaFiscalProps {
    open: boolean;
    handleClose: () => void;
    impostos: Imposto;
    onSave?: (impostos: Imposto) => void;
}